import { useIntl } from 'react-intl';
import { ENGLISH_LOCALE_CODE } from '#/shared/constants';

/**
 * This hook returns links to TriMet static pages that are in the language that
 * the user has selected in the app.  The static pages are hosted under a
 * different subdomain for each language, e.g. `es.trimet.org/...` for Spanish
 */
const useTriMetStaticPageLinks = () => {
  const { locale } = useIntl();
  const baseLocale = locale.split('-')[0];

  const links = {
    // locale subdomain pages
    ...Object.entries({
      about: 'about/index.htm',
      baseRouteSchedule: 'schedules/',
      careers: 'careers/index.htm',
      fares: 'fares/index.htm',
      fx: 'fx/index.htm',
      howToRide: 'howtoride/index.htm',
      max: 'max/index.htm',
      privacyPolicy: 'legal/privacy.htm',
      projects: 'bettertransit/index.htm',
      riderNews: 'ridernews/index.htm',
      schedules: 'schedules/index.htm',
      search: 'search/index.htm',
      serviceUpdates: 'emailupdates/index.htm',
      services: 'services/index.htm',
      siteFeedback: 'newsite/feedback',
      stopSchedule: 'ride/stop_schedule.html',
      termsOfUse: 'legal/terms.htm',
    }).reduce(
      (obj, [key, path]) => ({
        ...obj,
        // prepend the domain to the page path to get the full page url
        [key]: `https://${
          locale === ENGLISH_LOCALE_CODE ? '' : `${baseLocale}.`
        }trimet.org/${path}`,
      }),
      {}
    ),
    // `support` subdomain pages
    ...Object.entries({
      support: '',
      howToPayCash: 'articles/4417245229083',
    }).reduce(
      (obj, [key, path]) => ({
        ...obj,
        [key]: `https://support.trimet.org/hc/${locale}${
          path ? '/' : ''
        }${path}`,
      }),
      {}
    ),
  };

  const getRouteScheduleLink = routeId =>
    `${links.baseRouteSchedule}r${String(routeId).padStart(3, '0')}.htm`;

  const getStopScheduleLink = (stopId, routeId = null) =>
    `${links.stopSchedule}?` +
    `_LOCALE_=${baseLocale}&` +
    `stop_id=${stopId}` +
    `${routeId ? `&route=${routeId}` : ''}`;

  return {
    trimetLinks: links,
    getRouteScheduleLink,
    getStopScheduleLink,
  };
};

export default useTriMetStaticPageLinks;
